export interface GroundTruthPlanItem {
    task: string;
    sub_query: string;
}

export interface GroundTruthVizItem {
    chart_type: string;
    chartTitle: string;
    [key: string]: unknown;
}

export interface GroundTruthNarrative {
    overview?: string;
    key_takeaways?: string[];
    assumptions?: string[];
    formulae?: string[];
}

export interface GroundTruthPerTaskItem {
    task: string;
    task_id?: string;
    sub_query?: string;
    sql_queries?: string[];
    sql_db_output?: TaskResultSqlOutput[];
    viz?: GroundTruthVizItem | GroundTruthVizItem[];
    narrative?: GroundTruthNarrative;
}

export interface GroundTruth {
    GroundTruthIntent?: string;
    GroundTruthRefine?: string;
    GroundTruthTasks?: string[];
    GroundTruthPlan?: GroundTruthPlanItem[];
    GroundTruthSummary?: string;
    GroundTruthViz?: GroundTruthVizItem[];
    GroundTruthPerTask?: GroundTruthPerTaskItem[];
}

export interface TaskResultSqlOutput {
    columns: string[];
    rows: Record<string, unknown>[];
}

export interface TaskResult {
    task: string;
    task_name: string;
    success: boolean;
    sql_db_output?: TaskResultSqlOutput[];
}

export interface GroundTruthMetadata {
    source_thread_id?: string;
    source_run_id?: string;
    task_results?: TaskResult[];
    [key: string]: unknown;
}

export type GroundTruthStatus = 'draft' | 'reviewed' | 'approved';

export interface GroundTruthComment {
    author: string;
    text: string;
    created_at: string;
}

export interface CommentCreateRequest {
    text: string;
}

export interface EvalGroundTruthRequest {
    question_set: string;
    question: string;
    ground_truth?: GroundTruth;
    metadata?: GroundTruthMetadata;
    initial_comment?: CommentCreateRequest | null;
    status?: GroundTruthStatus;
}

export interface EvalGroundTruthResponse extends EvalGroundTruthRequest {
    id: string;
    comments?: GroundTruthComment[] | null;
    version: number;
    created_by?: string;
    updated_by?: string;
    created_at?: string;
    updated_at?: string;
}

export interface EvalGroundTruthUpdateRequest {
    question_set?: string;
    question?: string;
    ground_truth?: Partial<GroundTruth>;
    metadata?: Partial<GroundTruthMetadata>;
    comments?: GroundTruthComment[] | null;
    status?: GroundTruthStatus;
}

export interface QuestionSetInfo {
    question_set: string;
    count: number;
}

export interface EvalGroundTruthHistoryEntry extends EvalGroundTruthResponse {
    change_type: 'created' | 'updated' | 'deleted';
    changed_by?: string;
    changed_at?: string;
}

export interface EvalListParams {
    question_set?: string;
    status?: GroundTruthStatus;
    offset?: number;
    limit?: number;
}

export interface EvalExportParams {
    question_set?: string;
    status?: GroundTruthStatus;
}

/** Draft response from the from-chat endpoint (same shape as EvalGroundTruthRequest + metadata) */
export interface EvalFromChatDraft {
    question_set: string;
    question: string;
    ground_truth: GroundTruth;
    metadata: GroundTruthMetadata;
    comments?: GroundTruthComment[] | null;
    status: GroundTruthStatus;
}

export const INTENT_OPTIONS = [
    { value: 'enterprise_data', label: 'Enterprise Data' },
    { value: 'history', label: 'History' },
    { value: 'learning', label: 'Learning' },
    { value: 'out_of_scope', label: 'Out of Scope' },
] as const;

export const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'approved', label: 'Approved' },
] as const;

export const CHART_TYPE_OPTIONS = [
    { value: 'table', label: 'Table' },
    { value: 'Bar', label: 'Bar Chart' },
    { value: 'VerticalBar', label: 'Vertical Bar' },
    { value: 'Line', label: 'Line Chart' },
    { value: 'horizontalbar', label: 'Horizontal Bar' },
    { value: 'stackbar', label: 'Stacked Bar' },
    { value: 'waterfall', label: 'Waterfall' },
    { value: 'bpl', label: 'Brand Price Ladder' },
] as const;

// --- Eval Runs ---

export type EvalRunStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface EvalRunResponse {
    id: number;
    status: EvalRunStatus;
    config_name: string;
    config_snapshot: Record<string, unknown> | null;
    question_set: string | null;
    target_agent: string | null;
    question_count: number | null;
    output_dir: string | null;
    error_message: string | null;
    overview_metrics: Record<string, unknown> | null;
    triggered_by: string | null;
    started_at: string;
    completed_at: string | null;
    repeats: number | null;
}

export interface EvalPerQuestionResult {
    question_index: number;
    question_set: string;
    query: string;
    intent: string | null;
    error: string | null;
    row_pass: boolean | null;
    ground_truth: Record<string, unknown>;
    agent_outputs?: Record<string, unknown>;
    agent_evaluations?: Record<string, Record<string, Record<string, unknown>>>;
    gate_metrics?: Record<string, boolean>;
    combined_evaluations?: Record<string, Record<string, unknown>>;
}

export interface EvalRunResultsResponse {
    run: EvalRunResponse;
    overview_metrics: Record<string, unknown> | null;
    agents_evaluated: string[] | null;
    gate_results: Array<Record<string, unknown>> | null;
    per_question_results: EvalPerQuestionResult[] | null;
}

export interface TriggerEvalRunPayload {
    config_name: string;
    question_set?: string;
    target_agent?: string;
    question_ids?: number[];
    repeats?: number;
    tenant?: string;
    triggered_by?: string;
}

export interface EvalRunListParams {
    status?: EvalRunStatus;
    question_set?: string;
    target_agent?: string;
    offset?: number;
    limit?: number;
}

// --- Consistency metrics (returned when repeats > 1) ---

export interface ConsistencyFieldOutput {
    run_index: number;
    output: string;
}

export interface ConsistencyFieldDetail {
    exact_match_rate: number;
    unique_outputs: number;
    outputs_per_run: ConsistencyFieldOutput[];
}

export interface ConsistencyPerQuestion {
    group: string;
    query: string;
    runs: number;
    fields: Record<string, ConsistencyFieldDetail>;
}

export interface ConsistencyAggregate {
    mean_exact_match: number;
    min_exact_match: number;
}

export interface ConsistencyMetrics {
    aggregate: Record<string, ConsistencyAggregate>;
    per_question: ConsistencyPerQuestion[];
}

// --- Dashboard types ---

export interface DashboardMetricPoint {
    runId: number;
    startedAt: string;
    metricKey: string;
    value: number;
}

export interface DashboardRunSummary {
    runId: number;
    startedAt: string;
    configName: string;
    questionSet: string | null;
    targetAgent: string | null;
    repeats: number | null;
    status: EvalRunStatus;
    durationMs: number | null;
    passRate: number | null;
    errorRate: number | null;
    questionCount: number | null;
    metrics: Record<string, number>;
    consistency: ConsistencyMetrics | null;
    tenant?: string;
    configSnapshot?: Record<string, unknown>;
}

export interface DashboardFilters {
    questionSet?: string;
    targetAgent?: string;
    configName?: string;
    tenant?: string;
    dateRange?: [string, string];
}

export interface PromptChangePoint {
    sha: string;
    author: string;
    date: string;
    message: string;
    formattedDate: string;
}

export interface PromptImpactRow {
    sha: string;
    author: string;
    date: string;
    message: string;
    avgPassRateBefore: number | null;
    avgPassRateAfter: number | null;
    avgSimilarityBefore: number | null;
    avgSimilarityAfter: number | null;
    runsBefore: number;
    runsAfter: number;
}

export interface DashboardInsight {
    type: 'success' | 'warning' | 'info' | 'error';
    message: string;
}

export const TARGET_AGENT_OPTIONS = [
    { value: '', label: 'Full pipeline' },
    { value: 'intent', label: 'Intent' },
    { value: 'planner', label: 'Planner' },
    { value: 'refine', label: 'Refine' },
    { value: 'sql', label: 'SQL' },
    { value: 'narrative', label: 'Narrative' },
    { value: 'visualization', label: 'Visualization' },
    { value: 'execution', label: 'Execution' },
    { value: 'retrieval', label: 'Retrieval' },
] as const;
